import { FC, useCallback, useEffect, useMemo } from 'react'
import moment from 'moment'
import { AdvisorRequestPanePreset } from 'components/features/coaching/advisorMeeting/AdvisorRequestPanePreset/AdvisorRequestPanePreset'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import {
  AdvisorsRequestAction,
  cancelAdvisorMeetingActionCreator,
  deleteAdvisorMeetingActionCreator,
} from 'components/features/coaching/advisorStore/actions/advisorMeetingActions'
import { MeetingUid } from 'components/features/coaching/advisorPage/types/interfaces'
import { MeetingDetails } from 'components/features/coaching/advisorPage/MeetingDetails/MeetingDetails'
import { MeetingPaneError } from 'components/features/coaching/advisorMeeting/MeetingPaneError/MeetingPaneError'
import { hadAppointedMeetingSelector, isMemberPowerUserSelector } from 'selectors/globalSelectors'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import Button from 'components/features/pfm/Button/Button'
import { useForm, FormProvider } from 'react-hook-form'
import { useMutableRef } from 'hooks/useMutableRef'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { COACH_REVIEW_PAGE } from 'constants/pages'
import { DEFAULT_MEETING_DURATION } from 'components/features/coaching/advisorMeeting/types/constants'
import { IS_STATUS_APP } from 'helpers/userAgentHelpers'
import { nextScheduledMeetingSelector, reviewByMeetingUidSelector, requestByMeetingUidSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import { GetPublicAdvisorReviewsAction } from 'components/features/coaching/advisorStore/actions/publicAdvisorActions'
import { FLOR_REGULAR } from 'components/features/coaching/advisorMeeting/types/requests'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { SimpleGuard } from 'interfaces/guards'
import { GetMeetingAction } from 'components/features/coaching/advisorStore/actions/advisorPageActions'
import { useSimpleGuard } from 'helpers/loadableHelpers'

const meetingGuard: SimpleGuard<MeetingDetailsPaneProps> = {
  name: `Meeting Pane Guard`,
  checker: (state, { uid }) => {
    const { advisors } = state.data
    if (!advisors.meetings.data || advisors.meetings.isLoading) {
      return true
    }

    return !!advisors.meetings.data.byKey[uid]
  },
  loader: (dispatch, { uid }) => {
    if (!uid) {
      return
    }

    dispatch(new GetMeetingAction({ uid }))
  },
}


type MeetingDetailsPaneProps = {
  uid: MeetingUid
  onClose: () => void
}
export const MeetingDetailsPane: FC<MeetingDetailsPaneProps> = (props) => {
  const { uid, onClose } = props

  useSimpleGuard(meetingGuard, props, 'MeetingDetailsPane')

  const dispatch = useStoreDispatch()
  const meeting = useStoreSelector((state) => {
    return state.data.advisors.meetings.data?.byKey[uid] ?? null
  })

  const hadAppointedMeeting = useStoreSelector(hadAppointedMeetingSelector)
  const nextScheduledMeeting = useStoreSelector(nextScheduledMeetingSelector)
  const isMemberPowerUser = useStoreSelector(isMemberPowerUserSelector)
  const meetingReview = useStoreSelector((state) => reviewByMeetingUidSelector(state, props))
  const meetingRequest = useStoreSelector((state) => requestByMeetingUidSelector(state, props))

  useEffect(() => {
    if (meetingReview || meetingRequest) {
      return
    }
    dispatch(new GetPublicAdvisorReviewsAction({ meeting_uid: props.uid }))
  }, [dispatch, meetingReview, props.uid, meetingRequest])

  const handleRescheduleMeeting = useCallback(() => {
    if (!meeting || !meeting.topics || !meeting.notes) {
      return
    }
    const fields = {
      topics: meeting.topics.answer,
      notes: meeting.notes.answer,
      flow: FLOR_REGULAR,
    }

    dispatch(modifyFloatingPaneActionCreator('advisor', { mode: 'request', step: 'search' }))
    dispatch(new AdvisorsRequestAction(fields))
  }, [meeting, dispatch])

  const handleCancelMeeting = useCallback(() => {
    dispatch(cancelAdvisorMeetingActionCreator.request({ uid }))
  }, [uid, dispatch])

  const handleDeleteMeeting = useCallback(() => {
    dispatch(deleteAdvisorMeetingActionCreator.request({ uid }))
  }, [uid, dispatch])

  const formContext = useForm({
    defaultValues: {
      notes: meeting?.notes?.answer ?? '',
    },
  })
  const formContextRef = useMutableRef(formContext)

  const meetingChild = useMemo(() => {
    if (!meeting) {
      return null
    }
    return (
      <FormProvider { ...formContextRef.current }>
        <MeetingDetails
          advisorUid={ meeting.advisorUid }
          status={ meeting.status }
          topics={ meeting.topics && meeting.topics.answer }
          notes={ meeting.notes }
          timeslot={ meeting.timeslot }
          coach_notes={ meeting.coach_notes }
          review={ meetingReview }
          duration={ DEFAULT_MEETING_DURATION }
        />
      </FormProvider>
    )
  }, [meeting, formContextRef, meetingReview])

  const errorChild = useMemo(() => (
    <MeetingPaneError />
  ), [])

  const isMeetingTime = !meeting ? null : moment(meeting.timeslot).subtract(5, 'minutes').isBefore()
  const canScheduleSession = isMemberPowerUser && !nextScheduledMeeting && !hadAppointedMeeting

  const secondaryButton = useMemo(() => {
    switch (meeting?.status) {
      case 'scheduled': {
        return (
          <>
            { !IS_STATUS_APP && isMeetingTime && (
              <NavigationLink to='video' search={ `?meeting_uid=${ uid }` } >
                <Button fullWidth text='Join Session' onClick={ onClose } qaMarker='join-session' />
              </NavigationLink>
            )}
            { !IS_STATUS_APP && !isMeetingTime && (
              <Button fullWidth text='Join Session' onClick={ onClose } disabled qaMarker='join-session' />
            )}
            { !isMeetingTime && (
              <Button fullWidth text='Cancel This Session' onClick={ handleCancelMeeting } theme='danger' style='hollow' qaMarker='cancel-session' />
            )}
          </>
        )
      }

      case 'cancelled': {
        return (
          <>
            { canScheduleSession && (
              <Button fullWidth text='Schedule Another Session' onClick={ handleRescheduleMeeting } theme='blue' qaMarker='schedule-another-session' />
            )}
            <Button theme='danger' style='hollow' fullWidth onClick={ handleDeleteMeeting } text='Delete Session' qaMarker='delete-session' />
          </>
        )
      }

      case 'completed': {
        if (meeting.is_reviewed || meetingReview) {
          return null
        }

        return (
          <NavigationLink to={ COACH_REVIEW_PAGE } search={ `?meeting_uid=${ uid }` } >
            <Button fullWidth text='Leave Your Review' onClick={ onClose } qaMarker='leave-review' />
          </NavigationLink>
        )
      }
    }

    return null
  }, [
    canScheduleSession, handleCancelMeeting, handleDeleteMeeting, handleRescheduleMeeting, isMeetingTime,
    meeting?.is_reviewed, meetingReview, meeting?.status, onClose, uid,
  ])

  if (!meeting) {
    return (
      <LoadingOverlay />
    )
  }

  return (
    <AdvisorRequestPanePreset
      title='Coaching Session'
      secondaryButton={ secondaryButton }
      notification={ errorChild }
      onCancel={ onClose }
      cancelName='Close'
    >
      { meetingChild }
    </AdvisorRequestPanePreset>
  )
}
